/**
 * KYCFormPanel Component
 * TASK-373: AI-Assisted KYC Filling Workspace
 *
 * Right panel of the KYC workspace containing the KYC wizard in embedded mode
 * with an ExtractionStatusBar at the top. Receives pre-filled data from the
 * knowledge graph and exposes a method to apply extraction proposals.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import KYCWizard from '../KYCWizard';
import ExtractionStatusBar from './ExtractionStatusBar';
import ReviewBanner from './ReviewBanner';
import type { KYCFormData } from '../../../types/kyc.types';
import type { KnowledgeGraphEntry, KYCReview, KYCReviewComment } from '../../../types/complianceWorkflow';
import type { ClientExtractionStatus } from '../../../services/documentService';
import { buildFormDataFromKGEntries, KG_TO_FORM_FIELD_MAP } from '../../../utils/kgFieldMapping';
import { useKYCBridge } from '../../../stores/kycBridge';

interface KYCFormPanelProps {
  clientId: string;
  clientName: string;
  knowledgeEntries: KnowledgeGraphEntry[]; // Now: only verified entries
  totalExtracted?: number; // Total (verified + unverified)
  onFieldUpdate?: (fieldPath: string, value: unknown) => void;
  latestReview?: KYCReview | null;
  reviewComments?: KYCReviewComment[];
  profileStatus?: string | null;
  // TASK-817: the extraction's own state, so the bar can tell "not read yet" from
  // "nothing found" — counting fields alone never could.
  extraction?: ClientExtractionStatus | null;
  onRetryExtraction?: (documentId: string) => void;
  retryingIds?: string[];
}

/** Total extractable KYC fields derived from the canonical field mapping. */
const MAPPED_FIELD_COUNT = Object.keys(KG_TO_FORM_FIELD_MAP).length;

/**
 * Compute extraction status counts.
 * `entries` now contains only verified entries; `totalExtracted` is the full count.
 */
function computeExtractionStatus(
  entries: KnowledgeGraphEntry[],
  totalExtracted: number
): {
  autoFilled: number;
  needsReview: number;
  missing: number;
  total: number;
} {
  const verified = Array.isArray(entries) ? entries.length : 0;
  const pendingReview = Math.max(0, totalExtracted - verified);
  const total = Math.max(MAPPED_FIELD_COUNT, totalExtracted);
  const missing = Math.max(0, total - verified - pendingReview);

  return {
    autoFilled: verified,
    needsReview: pendingReview,
    missing,
    total,
  };
}

/**
 * Convert knowledge graph entries to partial KYC form data for prefilling.
 * Uses the KG→Form field mapping to translate backend paths to form paths.
 */
function kgEntriesToPrefillData(
  entries: KnowledgeGraphEntry[]
): Partial<KYCFormData> {
  const safeEntries = Array.isArray(entries) ? entries : [];
  if (safeEntries.length === 0) return {} as Partial<KYCFormData>;

  const { data, confidence, needsReconfirm } = buildFormDataFromKGEntries(safeEntries);

  // Attach confidence map for ConfidenceBadge usage
  data._confidence = confidence;
  // TASK-617: amber Re-confirm badges after an accepted transition
  data._needsReconfirm = needsReconfirm;

  return data as Partial<KYCFormData>;
}

const KYCFormPanel: React.FC<KYCFormPanelProps> = ({
  clientId,
  clientName,
  knowledgeEntries,
  totalExtracted = 0,
  onFieldUpdate,
  latestReview,
  reviewComments = [],
  profileStatus: _profileStatus,
  extraction,
  onRetryExtraction,
  retryingIds,
}) => {
  const [appliedProposals, setAppliedProposals] = useState<
    Record<string, unknown>
  >({});

  const extractionStatus = useMemo(
    () => computeExtractionStatus(knowledgeEntries, totalExtracted),
    [knowledgeEntries, totalExtracted]
  );

  // Build prefill data from knowledge graph + applied proposals
  const prefillData = useMemo(() => {
    const kgData = kgEntriesToPrefillData(knowledgeEntries);

    // Overlay applied proposals onto the prefill data (paths already mapped)
    if (Object.keys(appliedProposals).length > 0) {
      for (const [formPath, value] of Object.entries(appliedProposals)) {
        const segments = formPath.split('.');
        let current: Record<string, unknown> = kgData as Record<string, unknown>;
        for (let i = 0; i < segments.length - 1; i++) {
          const seg = segments[i];
          if (!current[seg] || typeof current[seg] !== 'object') {
            current[seg] = {};
          }
          current = current[seg] as Record<string, unknown>;
        }
        current[segments[segments.length - 1]] = value;
      }
    }

    return kgData;
  }, [knowledgeEntries, appliedProposals]);

  /**
   * Apply an extraction proposal by updating form data.
   * Delegates to the KYCWizardContext's applyExtractionProposal (via window bridge)
   * which directly calls setFormData, and falls back to the local appliedProposals state.
   */
  const applyExtractionProposal = useCallback(
    (fieldPath: string, value: unknown) => {
      // Prefer the context method if available
      const contextApply = useKYCBridge.getState()._applyProposalFn;
      if (typeof contextApply === 'function') {
        contextApply(fieldPath, value);
      }

      // Also update local state for the prefill overlay
      setAppliedProposals((prev) => ({
        ...prev,
        [fieldPath]: value,
      }));
      onFieldUpdate?.(fieldPath, value);
    },
    [onFieldUpdate]
  );

  // Expose applyExtractionProposal to parent via store
  const setApplyProposal = useKYCBridge((s) => s.setApplyProposal);
  useEffect(() => {
    setApplyProposal(applyExtractionProposal);
    return () => { setApplyProposal(null); };
  }, [applyExtractionProposal, setApplyProposal]);

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Extraction status bar */}
      <ExtractionStatusBar
        autoFilled={extractionStatus.autoFilled}
        needsReview={extractionStatus.needsReview}
        missing={extractionStatus.missing}
        total={extractionStatus.total}
        extraction={extraction}
        onRetryExtraction={onRetryExtraction}
        retryingIds={retryingIds}
      />

      {/* Review banner (visible for CO decisions: changes_requested, reject, or approve) */}
      {latestReview && (latestReview.decision === 'changes_requested' || latestReview.decision === 'reject' || latestReview.decision === 'approve') && (
        <ReviewBanner review={latestReview} comments={reviewComments} />
      )}

      {/* KYC Wizard in embedded mode */}
      <div className="flex-1 overflow-hidden">
        <KYCWizard
          clientId={clientId}
          clientName={clientName}
          externalPrefillData={prefillData}
          reviewMode={false}
          embedded
          reviewComments={reviewComments}
          isChangesRequested={!!latestReview && (latestReview.decision === 'changes_requested' || latestReview.decision === 'reject')}
        />
      </div>
    </div>
  );
};

export default KYCFormPanel;
export type { KYCFormPanelProps };
